import {radialProgressiveBlur} from '@remotion/effects/radial-progressive-blur';
import {MacOSCursor} from '@remotion/mac-cursors';
import {Video} from '@remotion/media';
import {
	AbsoluteFill,
	Composition,
	Easing,
	HtmlInCanvas,
	Interactive,
	interpolate,
	useCurrentFrame,
	useVideoConfig,
} from 'remotion';

const BasicsVolumeKeyframesInner = ({
	captureStyle,
}: {
	readonly captureStyle: React.CSSProperties | null;
}) => {
	const frame = useCurrentFrame();
	const {fps} = useVideoConfig();

	const screenCapture = (
		<>
			<Video
				name="Screen recording"
				premountFor={fps}
				muted
				effects={
					HtmlInCanvas.isSupported()
						? []
						: [
								radialProgressiveBlur({
									center: [0.64, 0.78],
									width: 1.5,
									height: 0.8,
									start: 0.46,
									startBlur: 0,
									endBlur: 28,
								}),
							]
				}
				src={
					'https://remotion.media/studio-close-ups/basics-showcase/volume-keyframes-6d2effc2371b.mp4'
				}
				durationInFrames={1785}
				style={{
					position: 'absolute',
				}}
			/>
			<MacOSCursor
				name="Recorded cursor"
				premountFor={fps}
				cursor={interpolate(
					frame,
					[
						7, 293, 295, 470, 735, 854, 1127, 1170, 1225, 1249, 1250, 1251,
						1267, 1270, 1400, 1424, 1427, 1428, 1434, 1435, 1460, 1463, 1464,
						1475, 1657,
					],
					[
						'auto',
						'default',
						'auto',
						'default',
						'auto',
						'default',
						'auto',
						'pointer',
						'auto',
						'ew-resize',
						'default',
						'auto',
						'default',
						'ew-resize',
						'auto',
						'pointer',
						'auto',
						'pointer',
						'auto',
						'pointer',
						'auto',
						'ew-resize',
						'auto',
						'default',
						'auto',
					],
					{
						easing: Easing.step1,
						extrapolateLeft: 'clamp',
						extrapolateRight: 'clamp',
					},
				)}
				style={{
					position: 'absolute',
					left: 0,
					top: 0,
					scale: interpolate(
						frame,
						[
							0, 335, 421, 517, 717, 767, 827, 884, 984, 1037, 1081, 1187, 1193,
							1286, 1378, 1446, 1452, 1518, 1669, 1722, 1727,
						],
						[
							2.924089791385738, 2.6316808122471644, 2.924089791385738,
							2.6316808122471644, 2.924089791385738, 2.6316808122471644,
							2.924089791385738, 2.6316808122471644, 2.924089791385738,
							2.6316808122471644, 2.924089791385738, 2.6316808122471644,
							2.924089791385738, 2.6316808122471644, 2.924089791385738,
							2.6316808122471644, 2.924089791385738, 2.6316808122471644,
							2.924089791385738, 2.6316808122471644, 2.924089791385738,
						],
						{
							easing: Easing.step1,
							extrapolateLeft: 'clamp',
							extrapolateRight: 'clamp',
						},
					),
					translate: interpolate(
						frame,
						[
							7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 292, 293, 294,
							295, 296, 297, 298, 299, 300, 301, 302, 303, 304, 305, 306, 307,
							308, 309, 310, 311, 312, 313, 314, 315, 316, 317, 318, 319, 320,
							321, 322, 323, 324, 325, 326, 327, 328, 329, 330, 331, 332, 333,
							347, 348, 349, 350, 351, 352, 353, 354, 355, 356, 357, 358, 359,
							360, 361, 362, 363, 364, 365, 366, 367, 368, 369, 370, 371, 372,
							373, 374, 375, 376, 377, 378, 379, 380, 381, 382, 383, 384, 385,
							386, 387, 388, 403, 404, 406, 407, 408, 409, 410, 411, 412, 413,
							414, 415, 430, 431, 432, 433, 434, 435, 436, 437, 438, 439, 440,
							441, 442, 443, 444, 445, 446, 447, 448, 449, 450, 451, 452, 453,
							454, 455, 456, 457, 458, 459, 466, 467, 468, 469, 470, 471, 472,
							473, 474, 475, 476, 477, 478, 479, 480, 481, 482, 483, 484, 485,
							488, 489, 492, 493, 506, 507, 508, 509, 510, 511, 512, 513, 514,
							536, 537, 538, 539, 540, 541, 542, 543, 544, 545, 546, 547, 548,
							549, 550, 551, 552, 553, 554, 555, 556, 557, 558, 559, 560, 561,
							562, 563, 564, 565, 566, 567, 568, 569, 570, 571, 572, 573, 574,
							575, 576, 577, 578, 579, 580, 581, 582, 583, 584, 585, 586, 587,
							588, 589, 590, 591, 592, 593, 594, 595, 596, 597, 598, 599, 600,
							601, 602, 603, 604, 605, 606, 607, 608, 609, 610, 611, 612, 613,
							614, 615, 616, 617, 618, 619, 620, 621, 622, 625, 626, 637, 638,
							639, 640, 641, 642, 643, 644, 645, 646, 647, 648, 649, 650, 651,
							652, 653, 654, 655, 656, 657, 658, 659, 660, 661, 662, 663, 664,
							665, 666, 667, 668, 669, 670, 671, 672, 673, 674, 675, 676, 677,
							678, 679, 680, 681, 682, 683, 684, 685, 686, 687, 688, 689, 690,
							691, 692, 693, 694, 695, 696, 697, 698, 699, 700, 701, 702, 703,
							704, 705, 706, 707, 708, 709, 732, 733, 734, 735, 736, 737, 738,
							739, 740, 741, 742, 743, 744, 745, 746, 747, 748, 749, 750, 751,
							752, 753, 754, 755, 756, 757, 758, 759, 760, 761, 762, 763, 764,
							765, 766, 783, 784, 785, 786, 787, 788, 789, 790, 791, 792, 793,
							794, 795, 796, 797, 798, 799, 800, 801, 802, 803, 804, 805, 806,
							807, 808, 809, 810, 811, 812, 813, 814, 815, 816, 817, 818, 819,
							820, 821, 822, 823, 824, 826, 827, 836, 837, 838, 839, 840, 841,
							842, 843, 844, 845, 846, 847, 848, 849, 850, 851, 852, 853, 854,
							855, 856, 857, 858, 859, 860, 861, 872, 873, 874, 875, 876, 877,
							878, 879, 880, 898, 899, 900, 901, 902, 903, 904, 905, 906, 907,
							908, 909, 910, 911, 912, 913, 914, 915, 916, 917, 918, 919, 920,
							921, 922, 923, 924, 943, 944, 945, 946, 947, 948, 949, 950, 951,
							952, 953, 954, 955, 956, 957, 958, 959, 960, 961, 962, 963, 964,
							965, 966, 967, 968, 969, 970, 971, 972, 973, 974, 975, 976, 977,
							978, 979, 980, 981, 996, 997, 998, 999, 1000, 1001, 1002, 1003,
							1004, 1005, 1006, 1007, 1008, 1009, 1010, 1011, 1012, 1013, 1014,
							1015, 1016, 1017, 1018, 1019, 1020, 1021, 1022, 1023, 1024, 1025,
							1026, 1027, 1028, 1029, 1030, 1031, 1032, 1033, 1050, 1051, 1052,
							1053, 1054, 1055, 1056, 1057, 1058, 1059, 1060, 1061, 1062, 1063,
							1064, 1065, 1066, 1067, 1068, 1069, 1070, 1071, 1072, 1073, 1074,
							1075, 1076, 1078, 1079, 1093, 1094, 1095, 1096, 1097, 1098, 1099,
							1100, 1101, 1102, 1103, 1104, 1105, 1106, 1107, 1108, 1109, 1110,
							1111, 1124, 1125, 1126, 1127, 1128, 1129, 1130, 1131, 1132, 1133,
							1134, 1135, 1136, 1137, 1138, 1139, 1140, 1141, 1142, 1143, 1144,
							1145, 1146, 1147, 1148, 1149, 1150, 1151, 1152, 1153, 1154, 1155,
							1156, 1157, 1158, 1159, 1160, 1161, 1162, 1163, 1164, 1165, 1166,
							1167, 1168, 1169, 1170, 1171, 1172, 1173, 1174, 1175, 1176, 1177,
							1178, 1179, 1180, 1182, 1183, 1184, 1223, 1224, 1225, 1226, 1227,
							1228, 1229, 1230, 1231, 1232, 1233, 1237, 1238, 1239, 1240, 1241,
							1242, 1243, 1244, 1245, 1246, 1247, 1248, 1249, 1250, 1251, 1252,
							1253, 1254, 1255, 1257, 1258, 1259, 1260, 1261, 1262, 1263, 1264,
							1265, 1266, 1267, 1268, 1269, 1270, 1271, 1272, 1273, 1274, 1275,
							1278, 1279, 1302, 1303, 1304, 1305, 1306, 1307, 1308, 1309, 1310,
							1311, 1312, 1313, 1314, 1315, 1316, 1317, 1318, 1319, 1320, 1321,
							1322, 1323, 1324, 1325, 1326, 1327, 1328, 1329, 1330, 1331, 1332,
							1333, 1334, 1335, 1336, 1337, 1338, 1339, 1340, 1341, 1342, 1343,
							1344, 1345, 1346, 1347, 1348, 1349, 1350, 1351, 1352, 1353, 1354,
							1355, 1356, 1357, 1358, 1359, 1360, 1361, 1362, 1363, 1364, 1365,
							1366, 1367, 1368, 1369, 1370, 1372, 1373, 1399, 1400, 1401, 1402,
							1403, 1404, 1405, 1406, 1407, 1408, 1409, 1410, 1411, 1412, 1413,
							1414, 1415, 1416, 1417, 1418, 1419, 1420, 1421, 1422, 1423, 1424,
							1425, 1426, 1427, 1428, 1429, 1430, 1431, 1432, 1433, 1434, 1435,
							1436, 1437, 1438, 1439, 1440, 1443, 1444, 1458, 1459, 1460, 1461,
							1462, 1463, 1464, 1465, 1466, 1467, 1468, 1469, 1470, 1471, 1472,
							1473, 1474, 1475, 1476, 1477, 1478, 1479, 1480, 1481, 1485, 1486,
							1487, 1488, 1489, 1490, 1491, 1492, 1493, 1494, 1495, 1496, 1497,
							1498, 1499, 1500, 1501, 1502, 1503, 1504, 1505, 1506, 1507, 1508,
							1509, 1510, 1511, 1512, 1513, 1514, 1515, 1517, 1518, 1536, 1537,
							1538, 1539, 1540, 1541, 1542, 1543, 1544, 1545, 1546, 1547, 1548,
							1549, 1550, 1551, 1552, 1553, 1554, 1555, 1556, 1557, 1558, 1559,
							1560, 1561, 1562, 1563, 1564, 1565, 1566, 1567, 1568, 1569, 1570,
							1571, 1572, 1573, 1574, 1575, 1576, 1577, 1578, 1579, 1580, 1581,
							1582, 1583, 1584, 1585, 1586, 1587, 1588, 1589, 1590, 1591, 1592,
							1593, 1594, 1595, 1596, 1597, 1598, 1599, 1600, 1601, 1602, 1603,
							1604, 1605, 1606, 1607, 1608, 1609, 1610, 1611, 1612, 1613, 1614,
							1615, 1616, 1617, 1618, 1619, 1620, 1621, 1622, 1623, 1624, 1625,
							1626, 1627, 1628, 1629, 1630, 1631, 1632, 1633, 1634, 1635, 1636,
							1637, 1638, 1639, 1640, 1641, 1642, 1643, 1644, 1645, 1646, 1647,
							1648, 1649, 1650, 1652, 1653, 1654, 1655, 1656, 1657, 1659, 1660,
							1669, 1670, 1683, 1684, 1685, 1686, 1687, 1688, 1689, 1690, 1691,
							1692, 1693, 1694, 1695, 1696, 1697, 1698, 1699, 1700, 1701, 1702,
							1703, 1704, 1705, 1706, 1707, 1708, 1709, 1710, 1711, 1712, 1713,
							1714, 1715, 1716, 1717, 1718, 1719, 1720, 1721, 1722,
						],
						[
							'3136.9429681922743px 1313.2704053303721px',
							'3036.2788926786707px 1313.2704053303721px',
							'2986.7863885143174px 1308.3131593559135px',
							'2868.3379075117px 1286.770841595939px',
							'2728.6440866185453px 1257.655588165305px',
							'2500.1653049892916px 1205.8786388514318px',
							'2243.6563813754265px 1167.442849210756px',
							'2165.0600460061873px 1162.0515586578886px',
							'2098.833981121599px 1153.0165780915365px',
							'2060.089791385738px 1146.5515983183946px',
							'2047.4910763861349px 1146.5515983183946px',
							'2032.105338304117px 1142.6337748869676px',
							'2022.5906242563656px 1140.246529705719px',
							'2022.5906242563656px 1140.246529705719px',
							'2009.2608868089158px 1131.1315935591338px',
							'1980.20274450702px 1102.073451257238px',
							'1939.996509875466px 1069.1774411041486px',
							'1889.4645831680814px 1030.478940271278px',
							'1856.3172840485445px 1007.4517331641152px',
							'1820.8055841992546px 984.8471484096137px',
							'1800.0742444673594px 971.6773221226304px',
							'1791.1192194812406px 966.2174982152773px',
							'1784.9512175775365px 961.5914967874991px',
							'1777.9608154200048px 958.5074958356469px',
							'1763.6830332355041px 944.2297136511462px',
							'1752.192274133418px 929.9290870151503px',
							'1742.4034266677243px 920.1402395494567px',
							'1736.9436027603713px 911.1852145633379px',
							'1733.859601808519px 906.9818354882209px',
							'1731.1982232093283px 903.8978345363687px',
							'1731.1982232093283px 901.659078289839px',
							'1730.2730229237725px 900.7338780042834px',
							'1730.2730229237725px 899.8772110732133px',
							'1730.2730229237725px 899.8772110732133px',
							'1730.2730229237725px 898.0953438565876px',
							'1730.2730229237725px 898.0953438565876px',
							'1730.2730229237725px 896.0507654477672px',
							'1730.2730229237725px 892.0415642103594px',
							'1730.2730229237725px 889.8028079638297px',
							'1730.2730229237725px 883.6348060601254px',
							'1730.2730229237725px 881.3960498135957px',
							'1731.8036011739512px 877.1926707384787px',
							'1732.9115570714682px 874.9539144919489px',
							'1732.9115570714682px 874.0287142063933px',
							'1733.8253351312765px 874.0287142063933px',
							'1733.8253351312765px 872.9093360831284px',
							'1733.8253351312765px 872.0526691520585px',
							'1734.7391131910845px 872.0526691520585px',
							'1734.7391131910845px 871.1274688665028px',
							'1735.6528912508925px 871.1274688665028px',
							'1736.7608471484098px 870.2022685809471px',
							'1736.7608471484098px 869.2770682953915px',
							'1736.7608471484098px 868.3518680098358px',
							'1737.868803045927px 868.3518680098358px',
							'1737.868803045927px 867.4952010787658px',
							'1737.868803045927px 867.4952010787658px',
							'1737.1377805980806px 867.4952010787658px',
							'1737.1377805980806px 867.4952010787658px',
							'1726.229555009122px 867.4952010787658px',
							'1711.1179503450464px 867.4952010787658px',
							'1674.8409613706672px 867.4952010787658px',
							'1660.7116681208854px 867.4952010787658px',
							'1657.4106448798289px 867.4952010787658px',
							'1650.317442690569px 868.0663123661458px',
							'1643.5440628222416px 868.9115570714682px',
							'1638.6439279765211px 870.0423574204807px',
							'1635.1373046720078px 870.1108907749664px',
							'1631.9619259141748px 870.1108907749664px',
							'1631.0253034028715px 870.1108907749664px',
							'1629.9401919568495px 870.1108907749664px',
							'1628.9464583168083px 870.1108907749664px',
							'1628.9464583168083px 870.1108907749664px',
							'1628.009835805505px 870.1108907749664px',
							'1627.1874355516777px 870.1108907749664px',
							'1626.3307686206076px 870.1108907749664px',
							'1624.3318791147776px 870.1108907749664px',
							'1622.0131672880148px 870.1108907749664px',
							'1622.0131672880148px 870.1108907749664px',
							'1614.0632981676847px 870.1108907749664px',
							'1612.0301419846119px 870.5677798048704px',
							'1607.621162846038px 871.150313317998px',
							'1604.046006187039px 871.1960022209884px',
							'1600.6764495914967px 871.1960022209884px',
							'1595.867692551757px 871.1960022209884px',
							'1593.9716030776553px 871.1960022209884px',
							'1590.5677798048703px 871.1960022209884px',
							'1587.9863567859127px 871.1960022209884px',
							'1583.42888871262px 872.292535892758px',
							'1580.4362655667487px 872.292535892758px',
							'1578.483064963909px 873.1377805980804px',
							'1574.4738637265011px 873.3433806615373px',
							'1571.7439517728246px 873.3548028872849px',
							'1569.3110176885857px 873.3548028872849px',
							'1567.4491948917268px 873.3548028872849px',
							'1566.5354168319188px 873.3548028872849px',
							'1566.5354168319188px 873.3548028872849px',
							'1565.4503053858969px 873.3548028872849px',
							'1565.4503053858969px 873.3548028872849px',
							'1565.2903942254304px 873.3548028872849px',
							'1565.2903942254304px 873.3548028872849px',
							'1566.8209724756089px 873.3548028872849px',
							'1566.8209724756089px 873.3548028872849px',
							'1568.614261917982px 873.3548028872849px',
							'1568.614261917982px 873.3548028872849px',
							'1570.5446180693266px 873.3548028872849px',
							'1571.3784405489014px 873.3548028872849px',
							'1572.2122630284764px 873.3548028872849px',
							'1573.1488855397795px 873.3548028872849px',
							'1574.1197747283256px 873.3548028872849px',
							'1574.1197747283256px 873.3548028872849px',
							'1580.687554533196px 873.3548028872849px',
							'1608.6948520663125px 873.3548028872849px',
							'1627.7014357103196px 878.1064487982867px',
							'1682.2882525581026px 904.9601015308956px',
							'1702.15150313318px 915.3086380582216px',
							'1718.279685888792px 928.2157531530103px',
							'1729.313555960974px 941.2028238280321px',
							'1734.3279130641708px 953.2075830887602px',
							'1734.3279130641708px 964.9267867057985px',
							'1734.3279130641708px 973.858967240422px',
							'1728.4454668041565px 985.5781708574602px',
							'1712.899817561672px 1003.910843182359px',
							'1694.544300785278px 1022.2435155072579px',
							'1672.1110494169907px 1039.0341873562306px',
							'1655.0233996985803px 1051.3359244863966px',
							'1634.2920599666852px 1064.4829063218847px',
							'1618.9063218846672px 1073.140953438566px',
							'1609.9512968985484px 1076.624732291584px',
							'1600.699294042992px 1081.2164670421196px',
							'1598.4605377964624px 1083.432378837154px',
							'1596.2217815499328px 1083.432378837154px',
							'1595.1024034266677px 1084.5403347346712px',
							'1591.7442690568732px 1086.7562465297058px',
							'1587.5408899817562px 1089.3947806774015px',
							'1583.3375109066392px 1092.0333148250972px',
							'1581.0987546601095px 1094.2492266201318px',
							'1578.85999841358px 1095.3571825176489px',
							'1577.740620290315px 1096.4651384151662px',
							'1576.8154200047593px 1097.3789164749742px',
							'1576.8154200047593px 1097.3789164749742px',
							'1578.6429761243753px 1097.3789164749742px',
							'1584.7881335765844px 1097.3789164749742px',
							'1588.9686682002064px 1100.01745062267px',
							'1591.1845799952407px 1102.2333624177045px',
							'1593.4004917902753px 1103.3413183152218px',
							'1594.3142698500833px 1104.2550963750298px',
							'1594.3142698500833px 1105.1688744348378px',
							'1594.3142698500833px 1106.0141191401603px',
							'1594.3142698500833px 1106.0141191401603px',
							'1594.3142698500833px 1107.967319743px',
							'1590.1108907749663px 1109.0752756405172px',
							'1585.9075116998495px 1110.1832315380345px',
							'1581.7041326247324px 1112.8217656857303px',
							'1577.5007535496154px 1114.3523439359087px',
							'1575.2619973030858px 1115.460299833426px',
							'1574.1426191798207px 1116.568255730943px',
							'1572.0980407710003px 1117.6762116284604px',
							'1572.0980407710003px 1117.6762116284604px',
							'1572.0980407710003px 1118.4643452050448px',
							'1572.0980407710003px 1118.4643452050448px',
							'1571.3441738716588px 1118.4643452050448px',
							'1571.3441738716588px 1118.4643452050448px',
							'1570.5788847465694px 1118.4643452050448px',
							'1570.5788847465694px 1118.4643452050448px',
							'1569.8592845244707px 1118.4643452050448px',
							'1569.8592845244707px 1118.4643452050448px',
							'1568.0774173078448px 1118.4643452050448px',
							'1568.0774173078448px 1118.4643452050448px',
							'1567.1522170222893px 1119.572301102562px',
							'1566.0328388990245px 1119.572301102562px',
							'1565.1761719679544px 1120.4175458078846px',
							'1564.3195050368843px 1120.4175458078846px',
							'1564.3195050368843px 1120.4175458078846px',
							'1563.5999048147855px 1120.4175458078846px',
							'1563.5999048147855px 1120.4175458078846px',
							'1561.6923931149363px 1120.4175458078846px',
							'1560.7786150551283px 1120.4175458078846px',
							'1559.7049258348536px 1120.4175458078846px',
							'1557.5118584913143px 1120.4175458078846px',
							'1556.43816927104px 1120.4175458078846px',
							'1555.3302133735226px 1120.4175458078846px',
							'1552.5888791940986px 1120.4175458078846px',
							'1552.5888791940986px 1120.4175458078846px',
							'1549.287855953042px 1120.4175458078846px',
							'1548.1570556040297px 1120.4175458078846px',
							'1545.6213214880622px 1120.4175458078846px',
							'1543.7823431426987px 1120.4175458078846px',
							'1541.7263425081305px 1120.4175458078846px',
							'1540.5726977076229px 1120.4175458078846px',
							'1538.2425636551122px 1120.4175458078846px',
							'1536.0038074085826px 1120.4175458078846px',
							'1534.964384865551px 1120.4175458078846px',
							'1534.073451257238px 1120.4175458078846px',
							'1532.0631395256605px 1120.4175458078846px',
							'1530.989450305386px 1120.4175458078846px',
							'1528.7050051558658px 1120.4175458078846px',
							'1527.6313159355914px 1120.4175458078846px',
							'1525.929404299199px 1120.4175458078846px',
							'1523.78202585865px 1120.4175458078846px',
							'1522.6512255096375px 1120.4175458078846px',
							'1521.5661140636155px 1120.4175458078846px',
							'1520.5495359720792px 1120.4175458078846px',
							'1519.692869041009px 1120.4175458078846px',
							'1518.8476243356865px 1120.4175458078846px',
							'1516.8487348298565px 1120.4175458078846px',
							'1514.5871341318316px 1120.4175458078846px',
							'1514.5871341318316px 1120.4175458078846px',
							'1512.6225113032442px 1120.4175458078846px',
							'1510.7949551836282px 1120.4175458078846px',
							'1509.6413103831205px 1120.4175458078846px',
							'1507.3911319108433px 1120.4175458078846px',
							'1506.1917982073453px 1120.4175458078846px',
							'1504.0329975410486px 1120.4175458078846px',
							'1503.050686126755px 1121.1942571587215px',
							'1501.051796620925px 1121.4683905766638px',
							'1499.8867295946698px 1121.5026572539066px',
							'1497.6708177996352px 1121.9709685095581px',
							'1496.562861902118px 1122.4278575394624px',
							'1494.5068612675498px 1122.4278575394624px',
							'1493.6616165622274px 1122.4278575394624px',
							'1492.702149599429px 1122.4278575394624px',
							'1491.731260410883px 1122.4278575394624px',
							'1490.668993416356px 1122.4278575394624px',
							'1488.7729039422543px 1122.4278575394624px',
							'1487.927659236932px 1122.4278575394624px',
							'1486.979614499881px 1122.4278575394624px',
							'1485.9973030855874px 1122.4278575394624px',
							'1485.0149916712937px 1122.4278575394624px',
							'1485.0149916712937px 1122.4278575394624px',
							'1482.3650352978505px 1122.4278575394624px',
							'1482.3650352978505px 1122.4278575394624px',
							'1480.2519235345444px 1122.4278575394624px',
							'1479.2696121202507px 1122.4278575394624px',
							'1479.2696121202507px 1122.4278575394624px',
							'1477.522011580868px 1122.4278575394624px',
							'1475.9343222019513px 1122.8733243436186px',
							'1474.175299436821px 1123.467280082494px',
							'1473.055921313556px 1123.5129689854843px',
							'1472.0050765447768px 1123.5129689854843px',
							'1469.3436979455857px 1124.3125247878163px',
							'1469.3436979455857px 1124.3125247878163px',
							'1468.1900531450783px 1124.5181248512731px',
							'1466.0312524787817px 1124.6095026572539px',
							'1464.9575632585072px 1124.6095026572539px',
							'1463.9752518442135px 1124.6095026572539px',
							'1463.9752518442135px 1124.6095026572539px',
							'1462.2961846593164px 1124.6095026572539px',
							'1462.2961846593164px 1124.6095026572539px',
							'1461.359562148013px 1124.6095026572539px',
							'1460.4115174109622px 1124.6095026572539px',
							'1460.4115174109622px 1124.6095026572539px',
							'1459.5776949313874px 1124.6095026572539px',
							'1458.7667169033077px 1124.6095026572539px',
							'1458.7667169033077px 1124.6095026572539px',
							'1457.89862774649px 1124.6095026572539px',
							'1457.0876497184104px 1124.6095026572539px',
							'1457.0876497184104px 1124.6095026572539px',
							'1456.2652494645831px 1124.6095026572539px',
							'1455.351471404775px 1124.6095026572539px',
							'1455.351471404775px 1124.6095026572539px',
							'1454.5062266994528px 1124.6095026572539px',
							'1454.5062266994528px 1124.6095026572539px',
							'1455.3628936305229px 1124.6095026572539px',
							'1455.3628936305229px 1124.6095026572539px',
							'1457.3960498135957px 1124.6095026572539px',
							'1458.332672324899px 1124.6095026572539px',
							'1460.1488062187675px 1124.6095026572539px',
							'1460.1488062187675px 1124.6095026572539px',
							'1461.1996509875466px 1124.6095026572539px',
							'1463.2670738478623px 1124.6095026572539px',
							'1465.0146743872453px 1124.6095026572539px',
							'1466.0312524787817px 1124.6095026572539px',
							'1468.167208693583px 1124.6095026572539px',
							'1469.2865868168478px 1124.6095026572539px',
							'1470.3374315856272px 1124.6095026572539px',
							'1472.313476639962px 1124.6095026572539px',
							'1472.313476639962px 1124.6095026572539px',
							'1474.1067660823353px 1124.6095026572539px',
							'1475.1233441738718px 1124.6095026572539px',
							'1477.259300388673px 1124.6095026572539px',
							'1478.1845006742287px 1124.6095026572539px',
							'1480.0006345680972px 1124.6095026572539px',
							'1482.2393908146269px 1124.6095026572539px',
							'1483.3587689378917px 1124.6095026572539px',
							'1485.5632585071787px 1124.6095026572539px',
							'1486.6712144046958px 1124.6095026572539px',
							'1488.4188149440788px 1124.6095026572539px',
							'1490.6918378678513px 1124.6095026572539px',
							'1493.067660823352px 1124.6095026572539px',
							'1495.4549060046006px 1124.6095026572539px',
							'1497.7964622828588px 1124.6095026572539px',
							'1503.3705084476878px 1124.6095026572539px',
							'1504.7183310859048px 1124.6095026572539px',
							'1508.9559768382646px 1124.6095026572539px',
							'1511.5602443087175px 1124.6095026572539px',
							'1515.8778456413104px 1124.6095026572539px',
							'1518.1051796620925px 1124.6095026572539px',
							'1520.64091377806px 1124.6095026572539px',
							'1523.9876259221069px 1124.6095026572539px',
							'1526.4890933608312px 1124.6095026572539px',
							'1530.0528277940828px 1124.6095026572539px',
							'1531.1722059173476px 1124.6095026572539px',
							'1535.478385024193px 1124.6095026572539px',
							'1538.2425636551122px 1124.6095026572539px',
							'1540.686919965099px 1124.6095026572539px',
							'1544.0450543348934px 1124.6095026572539px',
							'1548.4426112477197px 1125.7060363290236px',
							'1548.4426112477197px 1125.7060363290236px',
							'1553.068612675498px 1125.7060363290236px',
							'1555.604346791465px 1125.7060363290236px',
							'1558.928214484017px 1125.7060363290236px',
							'1562.274926628064px 1125.7060363290236px',
							'1565.4617276116444px 1126.8025700007934px',
							'1567.4948837947172px 1126.8025700007934px',
							'1568.6028396922345px 1126.8025700007934px',
							'1569.676528912509px 1126.8025700007934px',
							'1570.7159514555406px 1126.8025700007934px',
							'1571.6639961925914px 1126.8025700007934px',
							'1572.4978186721664px 1126.8025700007934px',
							'1573.3430633774888px 1126.8025700007934px',
							'1574.2568414372968px 1126.8025700007934px',
							'1574.2568414372968px 1126.8025700007934px',
							'1575.136352819862px 1126.8025700007934px',
							'1575.9473308479417px 1126.8025700007934px',
							'1575.9473308479417px 1126.8025700007934px',
							'1576.7583088760214px 1126.8025700007934px',
							'1576.7583088760214px 1126.8025700007934px',
							'1577.626398032839px 1127.2023479019592px',
							'1578.425953835171px 1127.6021258031253px',
							'1578.425953835171px 1127.6021258031253px',
							'1579.2597763147457px 1127.6021258031253px',
							'1579.2597763147457px 1127.6021258031253px',
							'1580.1507099230587px 1127.6021258031253px',
							'1580.915999048148px 1127.6021258031253px',
							'1580.915999048148px 1127.6021258031253px',
							'1580.915999048148px 1126.8825255810264px',
							'1580.915999048148px 1115.1404775124931px',
							'1594.062980883636px 1091.610692472436px',
							'1610.191163639248px 1052.866502736575px',
							'1620.5397001665742px 1029.3367176965178px',
							'1631.7334813992227px 1001.3065757119061px',
							'1642.9272626318711px 973.2764337272944px',
							'1650.477353851035px 949.7466486872373px',
							'1655.2289997620371px 930.7172205917348px',
							'1659.1354009677164px 918.9751725232014px',
							'1659.1354009677164px 910.0201475370826px',
							'1659.1354009677164px 903.8521456333783px',
							'1659.1354009677164px 901.6133893868487px',
							'1659.1354009677164px 898.2552550170541px',
							'1659.1354009677164px 894.051875941937px',
							'1656.0514000158644px 887.8838740382328px',
							'1653.3900214166733px 883.6804949631157px',
							'1650.7286428174825px 879.4771158879988px',
							'1643.7268184342033px 870.5220909018799px',
							'1633.5039263901008px 860.2991988577775px',
							'1621.4649004521298px 850.2133735226462px',
							'1615.2968985484256px 845.587372094868px',
							'1611.0935194733086px 842.9259934956771px',
							'1604.9255175696042px 841.383993019751px',
							'1598.7575156659px 841.383993019751px',
							'1592.5895137621956px 839.8419925438249px',
							'1586.4215118584914px 839.8419925438249px',
							'1580.2535099547872px 838.2999920678989px',
							'1572.966129927818px 838.2999920678989px',
							'1570.7273736812883px 837.1806139446339px',
							'1568.4886174347585px 837.1806139446339px',
							'1568.4886174347585px 836.0612358213691px',
							'1567.563417149203px 836.0612358213691px',
							'1567.563417149203px 835.2045688902991px',
							'1567.563417149203px 835.2045688902991px',
							'1567.9060839216309px 835.2045688902991px',
							'1572.0866185452528px 835.2045688902991px',
							'1582.0924883001508px 835.2045688902991px',
							'1591.7100023796304px 835.2045688902991px',
							'1609.7000079321012px 835.2045688902991px',
							'1617.9696993733642px 835.2045688902991px',
							'1626.0566352026653px 835.2045688902991px',
							'1630.6597921789482px 835.2045688902991px',
							'1634.2006821607044px 835.2045688902991px',
							'1643.224240501309px 835.2045688902991px',
							'1650.340287142064px 836.7351471404776px',
							'1653.938288252558px 836.7351471404776px',
							'1657.4220671055764px 837.694614103276px',
							'1660.8715792813516px 837.8316808122472px',
							'1663.350202268581px 837.8316808122472px',
							'1667.5650035694457px 838.379947648132px',
							'1670.4548266835886px 838.9282144840168px',
							'1671.5970492583485px 839.3736812881733px',
							'1675.6976283017373px 839.9676370270485px',
							'1678.3018957721902px 840.013325930039px',
							'1682.6080748790355px 841.5439041802174px',
							'1685.4636313159356px 841.5439041802174px',
							'1687.7366542397083px 842.4691044657731px',
							'1691.8372332830968px 842.6290156262394px',
							'1694.4415007535497px 842.640437851987px',
							'1697.7653684461015px 843.4057269770763px',
							'1700.7351471404777px 844.1710161021655px',
							'1704.298881573729px 844.1710161021655px',
							'1706.8917268184343px 844.1710161021655px',
							'1711.1407947965417px 844.1710161021655px',
							'1714.875862616007px 845.2675497739351px',
							'1717.7542635044024px 845.2675497739351px',
							'1718.8622194019197px 845.2675497739351px',
							'1720.0044419766798px 845.2675497739351px',
							'1723.5339097326882px 845.2675497739351px',
							'1725.9554215911796px 845.2675497739351px',
							'1727.805822162291px 845.2675497739351px',
							'1728.7081779963514px 845.2675497739351px',
							'1729.7818672166259px 845.2675497739351px',
							'1729.7818672166259px 845.2675497739351px',
							'1731.2439121123186px 845.81581660982px',
							'1731.2439121123186px 845.81581660982px',
							'1731.2439121123186px 846.5925279606569px',
							'1731.2439121123186px 846.5925279606569px',
							'1731.2439121123186px 848.8655508844294px',
							'1740.7472039343222px 867.8721345284366px',
							'1751.0957404616483px 891.3790751169985px',
							'1757.80058697549px 906.7419687475212px',
							'1781.0105496946142px 966.7314983739193px',
							'1787.4641072420086px 989.1647497422067px',
							'1795.4368208138337px 1027.532006028397px',
							'1799.7658443721743px 1042.8948996589197px',
							'1803.6722455778536px 1054.6141032759579px',
							'1803.6722455778536px 1063.5462838105814px',
							'1803.6722455778536px 1069.6914412627905px',
							'1805.202823828032px 1075.8365987149996px',
							'1805.202823828032px 1080.0171333386215px',
							'1805.202823828032px 1084.1976679622433px',
							'1805.202823828032px 1087.5215356547951px',
							'1805.202823828032px 1091.7020702784168px',
							'1806.7334020782107px 1097.847227730626px',
							'1806.7334020782107px 1104.8147854366623px',
							'1808.2639803283892px 1110.9599428888714px',
							'1808.2639803283892px 1114.0325216149758px',
							'1809.7945585785676px 1120.177679067185px',
							'1811.325136828746px 1125.466169588324px',
							'1811.325136828746px 1127.6820813833585px',
							'1811.325136828746px 1129.897993178393px',
							'1811.325136828746px 1132.1139049734275px',
							'1811.325136828746px 1132.1139049734275px',
							'1811.325136828746px 1131.3943047513287px',
							'1811.325136828746px 1130.469104465773px',
							'1811.325136828746px 1128.2303482192433px',
							'1811.325136828746px 1127.1109700959785px',
							'1811.325136828746px 1126.185769810423px',
							'1811.325136828746px 1125.2605695248672px',
							'1811.325136828746px 1124.403902593797px',
							'1810.2057587054812px 1124.403902593797px',
							'1810.2057587054812px 1124.403902593797px',
							'1812.4445149520109px 1124.403902593797px',
							'1815.985404933767px 1124.403902593797px',
							'1821.5137621956058px 1124.403902593797px',
							'1826.2311414293647px 1124.403902593797px',
							'1829.337986832712px 1124.403902593797px',
							'1838.064567303879px 1124.403902593797px',
							'1842.405013087967px 1124.403902593797px',
							'1845.5347029428096px 1124.403902593797px',
							'1849.9208376298882px 1124.403902593797px',
							'1855.2664392797653px 1124.403902593797px',
							'1860.7491076386136px 1124.403902593797px',
							'1866.2203537717141px 1124.403902593797px',
							'1872.1713333862142px 1124.403902593797px',
							'1877.5854683905768px 1124.403902593797px',
							'1883.0567145236773px 1124.403902593797px',
							'1888.5736495597685px 1124.403902593797px',
							'1893.0854287300706px 1124.403902593797px',
							'1897.2888078051876px 1124.403902593797px',
							'1900.4984532402634px 1124.403902593797px',
							'1904.9074323788373px 1124.403902593797px',
							'1908.5054334893314px 1124.403902593797px',
							'1911.920678987864px 1124.403902593797px',
							'1914.6962798445309px 1121.319901641945px',
							'1917.5975251844213px 1121.319901641945px',
							'1918.7397477591815px 1121.319901641945px',
							'1919.8248592052034px 1121.319901641945px',
							'1919.8248592052034px 1121.319901641945px',
							'1918.5684143729675px 1121.319901641945px',
							'1913.8053462362182px 1121.319901641945px',
							'1906.0153882763545px 1121.319901641945px',
							'1901.343697945586px 1121.319901641945px',
							'1889.8072499405093px 1121.7539462203538px',
							'1882.0172919806457px 1122.6563020544143px',
							'1872.6510668676133px 1122.8162132148807px',
							'1868.915999048148px 1122.8162132148807px',
							'1865.5007535496154px 1122.8162132148807px',
							'1863.4904418180377px 1122.8162132148807px',
							'1862.5766637582296px 1122.8162132148807px',
							'1860.4178630919332px 1122.8162132148807px',
							'1859.2527960656778px 1122.8162132148807px',
							'1856.9455064646627px 1122.8162132148807px',
							'1854.649639089395px 1122.8162132148807px',
							'1852.8563496470215px 1122.8162132148807px',
							'1850.697548980725px 1122.8162132148807px',
							'1849.5324819544699px 1122.8162132148807px',
							'1847.2251923534545px 1122.8162132148807px',
							'1846.0601253271993px 1122.8162132148807px',
							'1842.9075910208614px 1122.8162132148807px',
							'1841.982390735306px 1122.8162132148807px',
							'1839.8007456175142px 1122.8162132148807px',
							'1837.4135004362656px 1122.8162132148807px',
							'1835.026255255017px 1122.8162132148807px',
							'1832.9588323947014px 1122.8162132148807px',
							'1831.10843182359px 1122.8162132148807px',
							'1828.7897199968272px 1122.8162132148807px',
							'1826.3796303640836px 1122.8162132148807px',
							'1823.992385182835px 1122.8162132148807px',
							'1821.73078448481px 1122.8162132148807px',
							'1819.9260728166892px 1122.8162132148807px',
							'1817.6302054414216px 1122.8162132148807px',
							'1816.442293963671px 1122.8162132148807px',
							'1815.3457602919013px 1122.8162132148807px',
							'1814.3634488776077px 1122.8162132148807px',
							'1814.3634488776077px 1122.8162132148807px',
							'1813.5067819465378px 1122.8162132148807px',
							'1813.5067819465378px 1122.8162132148807px',
							'1812.7871817244388px 1122.8162132148807px',
							'1795.688109780281px 1122.8162132148807px',
							'1775.8020147537084px 1122.8162132148807px',
							'1760.4162766716904px 1122.8162132148807px',
							'1736.8864916316334px 1122.8162132148807px',
							'1721.5007535496154px 1122.8162132148807px',
							'1706.1150154675975px 1122.8162132148807px',
							'1694.372967399064px 1122.8162132148807px',
							'1682.6309193305308px 1122.8162132148807px',
							'1667.8048703101451px 1124.3467914650591px',
							'1661.636868406441px 1124.3467914650591px',
							'1657.433489331324px 1125.8773697152376px',
							'1653.230110256207px 1125.8773697152376px',
							'1649.0267311810899px 1125.8773697152376px',
							'1644.8233521059728px 1125.8773697152376px',
							'1638.6553502022687px 1125.8773697152376px',
							'1634.4519711271516px 1127.407947965416px',
							'1628.2839692234475px 1127.407947965416px',
							'1624.0805901483304px 1128.9385262155945px',
							'1618.7578329499486px 1128.9385262155945px',
							'1616.5190767034187px 1130.0464821131118px',
							'1614.280320456889px 1130.0464821131118px',
							'1610.076941381772px 1130.0464821131118px',
							'1603.9089394780679px 1130.0464821131118px',
							'1597.7409375743634px 1130.0464821131118px',
							'1593.5375584992464px 1130.0464821131118px',
							'1588.2148013008646px 1130.0464821131118px',
							'1585.976045054335px 1130.0464821131118px',
							'1583.7372888078053px 1130.0464821131118px',
							'1583.7372888078053px 1130.0464821131118px',
							'1581.886888236694px 1130.0464821131118px',
							'1581.886888236694px 1130.0464821131118px',
							'1580.1050210200683px 1130.0464821131118px',
							'1580.1050210200683px 1130.0464821131118px',
							'1579.2940429919886px 1130.0464821131118px',
							'1579.2940429919886px 1130.0464821131118px',
							'1578.483064963909px 1130.0464821131118px',
							'1578.483064963909px 1130.0464821131118px',
							'1578.1175537399859px 1130.0464821131118px',
							'1575.1820417228525px 1130.0464821131118px',
							'1569.4937733005474px 1130.0464821131118px',
							'1563.6798603950187px 1130.0464821131118px',
							'1556.4495914967877px 1130.0464821131118px',
							'1553.8681684778298px 1130.0464821131118px',
							'1545.9525660347426px 1130.0464821131118px',
							'1542.3545649242485px 1130.0464821131118px',
							'1539.1106528119299px 1130.0464821131118px',
							'1538.2197192036172px 1130.0464821131118px',
							'1535.0100737685414px 1130.0464821131118px',
							'1533.8221622907909px 1130.0464821131118px',
							'1531.5148726897755px 1130.0464821131118px',
							'1529.1847386372651px 1130.0464821131118px',
							'1527.1173157769495px 1130.0464821131118px',
							'1526.2606488458794px 1130.0464821131118px',
							'1525.2326485285953px 1130.0464821131118px',
							'1524.1818037598161px 1130.0464821131118px',
							'1524.1818037598161px 1130.0464821131118px',
							'1523.2566034742604px 1130.0464821131118px',
							'1522.4342032204331px 1130.0464821131118px',
							'1521.577536289363px 1130.0464821131118px',
							'1520.6180693265646px 1130.0464821131118px',
							'1519.635757912271px 1130.0464821131118px',
							'1518.664868723725px 1130.0464821131118px',
							'1517.808201792655px 1130.0464821131118px',
							'1517.808201792655px 1130.0464821131118px',
							'1516.9972237645752px 1130.0464821131118px',
							'1516.9972237645752px 1130.0464821131118px',
							'1527.2086935829302px 1130.0464821131118px',
							'1560.5730149916715px 1130.0464821131118px',
							'1605.508051082732px 1130.0464821131118px',
							'1640.5857063536132px 1130.0464821131118px',
							'1685.5207424446737px 1130.0464821131118px',
							'1718.8850638534147px 1130.0464821131118px',
							'1757.252320139605px 1132.4223050686128px',
							'1768.9715237566431px 1134.3755056714524px',
							'1775.9390814626795px 1135.4834615689697px',
							'1780.1196160863014px 1136.591417466487px',
							'1782.3355278813358px 1136.591417466487px',
							'1784.5514396763704px 1136.591417466487px',
							'1786.767351471405px 1136.591417466487px',
							'1788.7890854287302px 1136.591417466487px',
							'1789.8970413262473px 1136.591417466487px',
							'1791.9187752835726px 1136.591417466487px',
							'1791.9187752835726px 1136.591417466487px',
							'1792.7640199888951px 1136.591417466487px',
							'1792.7640199888951px 1136.591417466487px',
							'1792.0444197667964px 1136.591417466487px',
							'1725.8183548822083px 1128.413103831205px',
							'1586.0217339573253px 1098.8066946934243px',
							'1317.0854287300706px 1058.1435710319663px',
							'1011.8835567541843px 1007.2689775521536px',
							'845.5416831918776px 994.7273736812882px',
							'712.8382644562545px 987.5770603632902px',
							'593.498849845324px 987.5770603632902px',
							'515.8619814388832px 987.5770603632902px',
							'487.83183945427146px 987.5770603632902px',
							'472.4461013722535px 987.5770603632902px',
							'463.4910763861347px 987.5770603632902px',
							'459.2876973110177px 987.5770603632902px',
							'455.0843182359007px 988.6850162608075px',
							'448.9163163321964px 988.6850162608075px',
							'444.71293725707943px 990.215594510986px',
							'438.54493535337514px 991.7461727611645px',
							'432.37693344967084px 993.276751011343px',
							'411.1201713333862px 1003.8879987308638px',
							'391.2340763068137px 1014.23653525819px',
							'367.70429126675657px 1027.3835170936782px',
							'346.9729515348616px 1040.5304989291665px',
							'323.4431664948045px 1053.6774807646545px',
							'301.009915126517px 1070.4681526136274px',
							'280.27857539462207px 1083.6151344491157px',
							'255.37812326485286px 1097.0248274767987px',
							'239.99238518283494px 1101.3538510351393px',
							'228.2503371143016px 1107.2134528436584px',
							'212.86459903228365px 1111.542476401999px',
							'201.1225509637503px 1115.4488776076782px',
							'192.16752597763147px 1115.4488776076782px',
							'191.04814785436662px 1116.5568335051955px',
							'184.88014595066232px 1116.5568335051955px',
							'181.52201158086777px 1116.5568335051955px',
							'180.59681129531214px 1116.5568335051955px',
							'179.6716110097565px 1116.5568335051955px',
							'177.4328547632268px 1116.5568335051955px',
							'174.04045371618943px 1116.5568335051955px',
							'171.80169746965973px 1116.5568335051955px',
							'169.56294122313003px 1116.5568335051955px',
							'167.32418497660032px 1116.5568335051955px',
							'165.0854287300706px 1116.5568335051955px',
							'162.84667248354089px 1116.5568335051955px',
							'160.60791623701118px 1116.5568335051955px',
							'158.36915999048148px 1116.5568335051955px',
							'157.24978186721663px 1116.5568335051955px',
							'156.324581581661px 1116.5568335051955px',
							'155.39938129610533px 1117.6647894027128px',
							'154.4741810105497px 1117.6647894027128px',
							'154.4741810105497px 1117.6647894027128px',
							'153.61751407947966px 1117.6647894027128px',
							'152.76084714840962px 1117.6647894027128px',
							'152.76084714840962px 1117.6647894027128px',
							'151.94986912033px 1117.6647894027128px',
							'151.94986912033px 1117.6647894027128px',
							'151.17315776949314px 1117.6647894027128px',
							'151.17315776949314px 1117.6647894027128px',
							'150.3964464186563px 1117.6647894027128px',
							'150.3964464186563px 1118.77274530023px',
							'150.3964464186563px 1118.77274530023px',
							'154.16578091536448px 1118.77274530023px',
							'163.0979614499881px 1118.77274530023px',
							'172.03014198461173px 1120.7259459030697px',
							'176.21067660823354px 1120.7259459030697px',
							'180.39121123185532px 1122.2565241532482px',
							'182.60712302688984px 1122.2565241532482px',
							'186.78765765051162px 1122.2565241532482px',
							'187.89561354802888px 1122.2565241532482px',
							'190.1115253430634px 1122.2565241532482px',
							'192.1332593003887px 1122.2565241532482px',
							'192.1332593003887px 1122.2565241532482px',
							'188.2953914491949px 1122.2565241532482px',
							'182.12738954549062px 1122.2565241532482px',
							'175.95938764178632px 1123.7871024034266px',
							'169.79138573808203px 1123.7871024034266px',
							'167.55262949155232px 1123.7871024034266px',
							'166.43325136828747px 1123.7871024034266px',
							'166.43325136828747px 1123.7871024034266px',
							'171.75600856666932px 1123.7871024034266px',
							'237.09113984294441px 1115.6087887681447px',
							'363.5351788688824px 1108.9039422543033px',
							'503.20615531054176px 1108.9039422543033px',
							'654.3907353057825px 1108.9039422543033px',
							'732.9642262235267px 1108.9039422543033px',
							'806.2606488458794px 1117.4934560164988px',
							'835.1246133100659px 1120.7145236773222px',
							'847.700483858174px 1122.2451019275006px',
							'851.8810184817959px 1122.2451019275006px',
							'854.0969302768303px 1123.353057825018px',
							'854.0969302768303px 1123.353057825018px',
							'853.3202189259936px 1123.353057825018px',
							'847.1522170222893px 1123.353057825018px',
							'832.3261680019037px 1123.353057825018px',
							'820.5841199333704px 1123.353057825018px',
							'801.5546918378678px 1123.353057825018px',
							'778.0249067978108px 1123.353057825018px',
							'762.6391687157928px 1123.353057825018px',
							'750.8971206472595px 1123.353057825018px',
							'744.7291187435552px 1124.8836360751964px',
							'735.7740937574364px 1124.8836360751964px',
							'729.6060918537321px 1126.4142143253748px',
							'724.2833346553502px 1126.4142143253748px',
							'720.0799555802332px 1127.522170222892px',
							'717.8411993337036px 1127.522170222892px',
							'716.7218212104386px 1127.522170222892px',
							'715.7966209248831px 1127.522170222892px',
							'714.8714206393274px 1127.522170222892px',
							'713.9462203537718px 1127.522170222892px',
							'713.9462203537718px 1127.522170222892px',
							'713.1923534544301px 1127.522170222892px',
							'713.1923534544301px 1127.522170222892px',
							'712.8268422305069px 1127.522170222892px',
							'707.9267073847863px 1127.522170222892px',
							'703.6662171809313px 1127.522170222892px',
							'692.2325692075831px 1127.522170222892px',
							'688.9543904180217px 1127.522170222892px',
							'679.3026096612994px 1127.522170222892px',
							'674.3453636868406px 1127.522170222892px',
							'670.0620290314905px 1127.522170222892px',
							'663.9397160307766px 1127.522170222892px',
							'659.839136987388px 1127.522170222892px',
							'655.407313397319px 1127.522170222892px',
							'651.6379789006108px 1127.522170222892px',
							'648.4626001427779px 1127.522170222892px',
							'645.9154438010629px 1127.522170222892px',
							'644.7161100975649px 1127.522170222892px',
							'643.5624652970572px 1127.522170222892px',
							'641.4493535337512px 1127.522170222892px',
							'640.5013087967003px 1127.522170222892px',
							'639.6446418656302px 1127.522170222892px',
							'639.6446418656302px 1127.522170222892px',
							'638.708019354327px 1127.522170222892px',
							'637.7828190687713px 1127.522170222892px',
							'636.9489965891966px 1127.522170222892px',
							'636.9489965891966px 1127.522170222892px',
							'636.1380185611168px 1127.522170222892px',
							'635.2585071785517px 1127.522170222892px',
							'635.2585071785517px 1127.522170222892px',
							'634.4361069247244px 1127.522170222892px',
							'633.6365511223923px 1127.522170222892px',
							'633.6365511223923px 1127.522170222892px',
							'632.7456175140795px 1127.522170222892px',
							'632.2658840326803px 1127.979059252796px',
							'630.7467280082494px 1128.4359482827px',
							'629.9129055286746px 1128.4359482827px',
							'628.9648607916238px 1128.4359482827px',
							'627.868327119854px 1129.4411041484889px',
							'626.7489489965892px 1129.498215277227px',
							'625.6752597763148px 1129.521059728722px',
							'623.9847703656699px 1129.521059728722px',
							'622.9681922741335px 1129.521059728722px',
							'621.894503053859px 1129.9436820813835px',
							'620.9007694138178px 1130.3891488855397px',
							'619.8270801935433px 1130.411993337035px',
							'618.7762354247641px 1130.411993337035px',
							'617.919568493694px 1130.411993337035px',
							'617.0971682398667px 1130.8231934639487px',
							'616.1719679543111px 1131.2686602681051px',
							'615.1896565400175px 1131.302926945348px',
							'614.344411834695px 1131.3143491710955px',
							'613.544856032363px 1131.3143491710955px',
							'613.544856032363px 1131.3143491710955px',
							'612.6082335210598px 1131.3143491710955px',
							'611.6487665582613px 1131.3143491710955px',
							'610.7349884984533px 1131.3143491710955px',
							'609.9240104703737px 1131.3143491710955px',
							'609.9240104703737px 1131.3143491710955px',
							'608.5876100579044px 1131.8169271039899px',
							'608.0964543507575px 1132.3651939398746px',
							'608.0964543507575px 1132.3651939398746px',
							'607.1826762909495px 1132.410882842865px',
							'606.3602760371223px 1132.410882842865px',
							'606.3602760371223px 1132.410882842865px',
							'605.5264535575474px 1132.410882842865px',
							'605.5264535575474px 1132.410882842865px',
							'604.6583644007297px 1132.410882842865px',
							'603.8473863726501px 1132.410882842865px',
							'603.8473863726501px 1132.410882842865px',
							'603.0364083445705px 1132.410882842865px',
							'603.0364083445705px 1132.410882842865px',
							'602.2368525422385px 1132.410882842865px',
							'602.2368525422385px 1132.410882842865px',
							'595.646228285873px 1132.410882842865px',
							'580.260490203855px 1132.410882842865px',
							'551.3736812881733px 1132.410882842865px',
							'522.4868723724915px 1132.410882842865px',
							'489.0997065122551px 1132.410882842865px',
							'455.71254065201873px 1135.6319505036886px',
							'427.682398667407px 1138.4303958118505px',
							'404.1526136273499px 1140.8062187673515px',
							'388.76687554533197px 1140.8062187673515px',
							'373.3811374633141px 1140.8062187673515px',
							'357.9953993812961px 1140.8062187673515px',
							'342.6096612992782px 1140.8062187673515px',
							'323.5802332037757px 1140.8062187673515px',
							'308.19449512175777px 1140.8062187673515px',
							'279.65035297850403px 1140.8062187673515px',
							'260.6209248830015px 1140.8062187673515px',
							'245.2351868009836px 1140.8062187673515px',
							'233.49313873245023px 1140.8062187673515px',
							'221.75109066391687px 1140.8062187673515px',
							'212.79606567779805px 1140.8062187673515px',
							'201.0540176092647px 1140.8062187673515px',
							'189.31196954073135px 1138.8415959387642px',
							'177.569921472198px 1136.876973110177px',
							'165.82787340366463px 1136.876973110177px',
							'150.4421353216467px 1136.876973110177px',
							'132.82906321884667px 1136.876973110177px',
							'123.87403823272786px 1135.3349726342508px',
							'114.91901324660903px 1135.3349726342508px',
							'108.75101134290475px 1135.3349726342508px',
							'102.58300943920045px 1133.7929721583248px',
							'98.37963036408345px 1132.2509716823988px',
							'92.21162846037916px 1130.7089712064726px',
							'83.25660347426033px 1129.1669707305466px',
							'77.08860157055604px 1127.6249702546204px',
							'70.92059966685176px 1124.5409693027684px',
							'65.5978424684699px 1122.9989688268422px',
							'63.359086221940196px 1121.8795907035774px',
							'61.12032997541049px 1121.8795907035774px',
							'60.00095185214563px 1120.7602125803126px',
							'60.00095185214563px 1120.7602125803126px',
							'59.189973824065994px 1120.7602125803126px',
							'59.189973824065994px 1120.7602125803126px',
							'58.43610692472436px 1120.7602125803126px',
							'58.43610692472436px 1120.7602125803126px',
							'78.50495756325851px 1120.7602125803126px',
							'187.19885777742525px 1120.7602125803126px',
							'350.81081938605536px 1120.7602125803126px',
							'565.9370191163639px 1120.7602125803126px',
							'687.2867454588721px 1120.7602125803126px',
							'827.0605219322599px 1120.7602125803126px',
							'989.758705481082px 1120.7602125803126px',
							'1118.807011977473px 1126.1400809074323px',
							'1232.6523360038075px 1131.5199492345523px',
							'1290.8942650908227px 1135.1636392480368px',
							'1324.2585864995638px 1135.1636392480368px',
							'1352.2658840326803px 1135.1636392480368px',
							'1371.2724676766877px 1135.1636392480368px',
							'1404.2827000872533px 1135.1636392480368px',
							'1429.5029745379552px 1135.1636392480368px',
							'1474.4380106290157px 1135.1636392480368px',
							'1514.0160228444515px 1131.5085270088048px',
							'1552.7373681288175px 1128.2760371222337px',
							'1580.7446656619338px 1125.466169588324px',
							'1596.1075592924567px 1125.466169588324px',
							'1603.075116998493px 1125.466169588324px',
							'1605.2910287935274px 1125.466169588324px',
							'1607.506940588562px 1125.466169588324px',
							'1607.506940588562px 1125.466169588324px',
							'1603.6804949631157px 1125.466169588324px',
							'1596.6900928055843px 1125.466169588324px',
							'1587.7350678194655px 1125.466169588324px',
							'1575.993019750932px 1125.466169588324px',
							'1564.2509716823988px 1125.466169588324px',
							'1555.29594669628px 1125.466169588324px',
							'1549.1279447925756px 1125.466169588324px',
							'1544.9245657174586px 1125.466169588324px',
							'1540.7211866423415px 1125.466169588324px',
							'1538.482430395812px 1125.466169588324px',
							'1534.2790513206949px 1125.466169588324px',
							'1530.0756722455778px 1125.466169588324px',
							'1524.752915047196px 1124.3467914650591px',
							'1524.752915047196px 1124.3467914650591px',
							'1522.902514476085px 1123.4215911795036px',
							'1521.9773141905291px 1123.4215911795036px',
							'1521.9773141905291px 1123.4215911795036px',
							'1520.1269136194178px 1122.4963908939478px',
							'1520.1269136194178px 1122.4963908939478px',
							'1519.270246688348px 1122.4963908939478px',
							'1519.270246688348px 1122.4963908939478px',
							'1518.4592686602682px 1122.4963908939478px',
							'1517.6482906321885px 1122.4963908939478px',
							'1517.6482906321885px 1122.4963908939478px',
							'1516.7916237011184px 1122.4963908939478px',
							'1516.7916237011184px 1122.4963908939478px',
							'1515.980645673039px 1122.4963908939478px',
							'1515.1239787419688px 1122.4963908939478px',
							'1515.1239787419688px 1122.4963908939478px',
							'1514.2673118108987px 1122.4963908939478px',
							'1514.2673118108987px 1122.4963908939478px',
							'1513.8675339097329px 1122.4963908939478px',
							'1513.8675339097329px 1122.4963908939478px',
							'1513.8218450067423px 1122.4963908939478px',
							'1514.5642896803363px 1122.4963908939478px',
							'1516.5403347346712px 1122.4963908939478px',
							'1518.8133576584437px 1122.4963908939478px',
							'1521.0064250019832px 1122.4963908939478px',
							'1521.0064250019832px 1122.4963908939478px',
							'1522.7882922186088px 1122.4963908939478px',
							'1523.8048703101451px 1122.4963908939478px',
							'1526.0550487824225px 1122.4963908939478px',
							'1526.0550487824225px 1122.4963908939478px',
							'1528.6821607043707px 1122.4963908939478px',
							'1530.9666058538908px 1122.4963908939478px',
							'1532.074561751408px 1122.4963908939478px',
							'1534.359006900928px 1122.4963908939478px',
							'1535.4326961212025px 1122.4963908939478px',
							'1538.5052748473072px 1122.4963908939478px',
							'1539.4190529071152px 1122.4963908939478px',
							'1541.5435868961688px 1122.4963908939478px',
							'1542.7086539224242px 1122.4963908939478px',
							'1544.9930990719442px 1122.4963908939478px',
							'1546.1238994209566px 1122.4963908939478px',
							'1548.8652336003809px 1122.4963908939478px',
							'1551.1496787499009px 1122.4963908939478px',
							'1552.3375902276514px 1122.4963908939478px',
							'1553.6511461886255px 1123.5358134369794px',
							'1555.9355913381455px 1123.5929245657176px',
							'1558.813992226541px 1124.0041246926312px',
							'1561.0870151503134px 1124.5980804315063px',
							'1563.4514158800666px 1124.666613785992px',
							'1565.8386610613152px 1124.666613785992px',
							'1568.1916395653209px 1125.7631474577615px',
							'1570.1791068454033px 1125.7631474577615px',
							'1573.76568573015px 1126.1971920361705px',
							'1576.4156421035932px 1126.7911477750456px',
							'1578.8485761878321px 1126.8596811295313px',
							'1581.2243991433331px 1126.8596811295313px',
							'1583.520266518601px 1126.8596811295313px',
							'1585.519156024431px 1127.9562148013008px',
							'1587.5523122075037px 1127.9562148013008px',
							'1589.8710240342668px 1127.9562148013008px',
							'1592.269691441263px 1127.9562148013008px',
							'1594.6112477195209px 1127.9562148013008px',
							'1599.6941381772033px 1129.4867930514795px',
							'1601.7158721345286px 1129.4867930514795px',
							'1604.0574284127865px 1129.4867930514795px',
							'1606.42182914254px 1129.4867930514795px',
							'1608.8090743237885px 1129.4867930514795px',
							'1611.0478305703182px 1129.4867930514795px',
							'1612.8182755611963px 1129.4867930514795px',
							'1615.0798762592212px 1129.4867930514795px',
							'1617.4785436662173px 1129.977948758626px',
							'1619.8429443959706px 1130.5490600460062px',
							'1622.173078448481px 1130.5719044975015px',
							'1625.0514793368764px 1130.5719044975015px',
							'1628.4895692869043px 1131.040215753153px',
							'1628.4895692869043px 1131.040215753153px',
							'1632.064725945903px 1131.6570159435234px',
							'1634.3948599984137px 1131.6570159435234px',
							'1636.3823272784962px 1131.6570159435234px',
							'1638.392639010074px 1131.6570159435234px',
							'1640.7113508368368px 1131.6570159435234px',
							'1643.1100182438329px 1131.6570159435234px',
							'1645.485841199334px 1131.6570159435234px',
							'1648.626953279924px 1132.7535496152932px',
							'1650.625842785754px 1132.7535496152932px',
							'1652.9445546125169px 1132.7535496152932px',
							'1655.3317997937654px 1133.7701277068295px',
							'1657.719044975014px 1133.8500832870627px',
							'1659.980645673039px 1133.8500832870627px',
							'1660.9058459585945px 1133.8500832870627px',
							'1662.7562465297058px 1133.8500832870627px',
							'1665.0521139049736px 1133.8500832870627px',
							'1666.240025382724px 1134.3526612199573px',
							'1668.4787816292537px 1134.9466169588325px',
							'1670.523360038074px 1134.9466169588325px',
							'1672.3737606091854px 1134.9466169588325px',
							'1673.4474498294599px 1134.9466169588325px',
							'1674.5097168239868px 1134.9466169588325px',
							'1676.6685174902832px 1134.9466169588325px',
							'1677.44522884112px 1134.9466169588325px',
							'1678.359006900928px 1134.9466169588325px',
							'1680.4378519869915px 1134.9466169588325px',
							'1680.4378519869915px 1134.9466169588325px',
							'1682.5281192988023px 1134.9466169588325px',
							'1683.5675418418339px 1134.9466169588325px',
							'1684.4927421273896px 1134.9466169588325px',
							'1686.2746093440153px 1134.9466169588325px',
							'1686.2746093440153px 1134.9466169588325px',
							'1688.4334100103117px 1134.9466169588325px',
							'1690.546521773618px 1134.9466169588325px',
							'1691.4260331561832px 1136.043150630602px',
							'1693.4249226620132px 1136.043150630602px',
							'1694.48718965654px 1136.043150630602px',
							'1695.606567779805px 1136.043150630602px',
							'1696.6688347743318px 1136.043150630602px',
							'1698.4278575394624px 1136.043150630602px',
							'1699.3416355992704px 1136.043150630602px',
							'1700.4153248195448px 1136.043150630602px',
							'1701.3976362338385px 1136.043150630602px',
							'1702.3685254223844px 1136.043150630602px',
							'1703.3051479336877px 1136.043150630602px',
							'1704.1389704132625px 1136.043150630602px',
							'1704.984215118585px 1136.043150630602px',
							'1705.9322598556357px 1136.043150630602px',
							'1706.9031490441819px 1136.043150630602px',
							'1706.9031490441819px 1136.043150630602px',
							'1708.6393273578171px 1136.043150630602px',
							'1708.6393273578171px 1136.043150630602px',
							'1709.4503053858969px 1136.043150630602px',
							'1709.4503053858969px 1136.043150630602px',
							'1710.329816768462px 1136.043150630602px',
							'1711.1407947965417px 1136.043150630602px',
							'1711.1407947965417px 1136.043150630602px',
							'1711.9403505988737px 1136.4315063060205px',
							'1711.9403505988737px 1136.4315063060205px',
							'1712.7855953041962px 1136.8312842071866px',
							'1713.5737288807807px 1136.8427064329342px',
							'1713.9506623304515px 1137.2539065598478px',
							'1713.9506623304515px 1137.2539065598478px',
							'1714.3847069088602px 1137.7107955897518px',
							'1714.3847069088602px 1137.7107955897518px',
							'1715.1728404854446px 1137.7450622669946px',
							'1715.1728404854446px 1137.7450622669946px',
							'1714.4418180375983px 1137.7450622669946px',
							'1714.4418180375983px 1137.7450622669946px',
							'1715.1499960339495px 1137.7450622669946px',
							'1738.6569366225115px 1137.7450622669946px',
							'1778.2349488379473px 1137.7450622669946px',
							'1851.5313714602999px 1142.2568414372968px',
							'2002.7159514555406px 1172.217339573253px',
							'2203.5986356785916px 1228.003490124534px',
							'2496.692948362021px 1323.6303640834458px',
							'2697.575632585072px 1378.9710478305703px',
							'2848.760212580313px 1421.4503053858969px',
							'2947.7223764575238px 1439.942888871262px',
							'2987.30038867296px 1439.942888871262px',
							'3032.23542476402px 1439.942888871262px',
							'3046.2390735305785px 1439.942888871262px',
							'3065.2456571745856px 1439.942888871262px',
							'3076.964860791624px 1439.942888871262px',
							'3083.110018243833px 1439.942888871262px',
							'3087.2905528674546px 1438.400888395336px',
							'3091.459665265329px 1431.1135083683669px',
							'3092.9902435155072px 1422.158483382248px',
							'3094.520821765686px 1415.9904814785436px',
							'3094.520821765686px 1409.8224795748395px',
							'3094.520821765686px 1400.8674545887206px',
							'3094.520821765686px 1393.877052431189px',
							'3095.628777663203px 1389.6736733560722px',
							'3097.581978266043px 1380.7186483699534px',
							'3099.1125565162215px 1374.550646466249px',
							'3100.6431347663997px 1368.3826445625448px',
							'3104.126913619418px 1356.3436186245738px',
							'3107.6106924724363px 1347.388593638455px',
							'3109.1412707226145px 1341.2205917347505px',
							'3112.2024272229714px 1335.0525898310464px',
							'3114.840961370667px 1330.8492107559293px',
							'3117.479495518363px 1326.6458316808123px',
							'3118.5874514158804px 1322.4424526056953px',
							'3120.8033632109145px 1320.2036963591656px',
							'3121.911319108432px 1319.0843182359008px',
							'3122.82509716824px 1317.0397398270802px',
							'3123.738875228048px 1317.0397398270802px',
							'3123.738875228048px 1315.9203617038154px',
						],
						{
							extrapolateLeft: 'clamp',
							extrapolateRight: 'clamp',
						},
					),
				}}
			/>
		</>
	);

	return (
		<AbsoluteFill
			showInTimeline={false}
			style={{backgroundColor: '#111518', overflow: 'hidden'}}
		>
			{HtmlInCanvas.isSupported() ? (
				<HtmlInCanvas
					name="Canvas Capture"
					width={2114}
					height={1440}
					effects={[
						radialProgressiveBlur({
							center: [0.64, 0.78],
							width: 1.5,
							height: 0.8,
							start: 0.46,
							startBlur: 0,
							endBlur: 28,
						}),
					]}
					durationInFrames={1785}
					premountFor={fps}
					style={{
						translate: interpolate(
							frame,
							[0, 1090, 1140, 1435, 1485],
							[
								'-865px -600px',
								'-865px -600px',
								'175px -600px',
								'175px -600px',
								'-865px -600px',
							],
							{
								easing: Easing.bezier(0.42, 0, 0.58, 1),
								extrapolateLeft: 'clamp',
								extrapolateRight: 'clamp',
							},
						),
						scale: 1.05,
						rotate: '-2deg',
						transformOrigin: '0 0',
						...captureStyle,
					}}
				>
					{screenCapture}
				</HtmlInCanvas>
			) : (
				<AbsoluteFill
					name="Canvas Capture"
					durationInFrames={1785}
					premountFor={fps}
					style={{
						width: 2114,
						height: 1440,
						translate: interpolate(
							frame,
							[0, 1090, 1140, 1435, 1485],
							[
								'-865px -600px',
								'-865px -600px',
								'175px -600px',
								'175px -600px',
								'-865px -600px',
							],
							{
								easing: Easing.bezier(0.42, 0, 0.58, 1),
								extrapolateLeft: 'clamp',
								extrapolateRight: 'clamp',
							},
						),
						scale: 1.05,
						rotate: '-2deg',
						transformOrigin: '0 0',
						...captureStyle,
					}}
				>
					{screenCapture}
				</AbsoluteFill>
			)}
		</AbsoluteFill>
	);
};

export const BasicsVolumeKeyframesPreview = Interactive.withSchema({
	Component: BasicsVolumeKeyframesInner,
	componentName: 'BasicsVolumeKeyframesPreview',
	schema: {},
	wrapInSequence: true,
	layout: 'absolute-fill',
});

export const BasicsVolumeKeyframesComposition = () => {
	return (
		<Composition
			id="BasicsVolumeKeyframes"
			component={BasicsVolumeKeyframesPreview}
			width={1920}
			height={1080}
			defaultProps={{captureStyle: null}}
			fps={60}
			durationInFrames={1785}
		/>
	);
};
